import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuditForkPicker } from '../components/audit/AuditForkPicker';
import { useAuth } from '../lib/auth';
import { createAudit, getCurrentAudit } from '../lib/api-client';
import { getTargetWorkspaceId } from '../lib/workspace';
import type { AuditFork } from '@vibecontrol/shared/roleforge';

export function AuditForkLandingPage() {
  const nav = useNavigate();
  const { jwt } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [checkingCurrent, setCheckingCurrent] = useState(true);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jwt) return;
    let cancelled = false;
    let redirected = false;

    void (async () => {
      try {
        const ws = await getTargetWorkspaceId();
        if (!ws) throw new Error('no workspace; check signup webhook');
        if (cancelled) return;
        setWorkspaceId(ws);
        const current = await getCurrentAudit(jwt, ws);
        if (cancelled) return;
        if (current) {
          redirected = true;
          nav(`/audits/${current.id}`, { replace: true });
          return;
        }
      } catch (e) {
        if (!cancelled) {
          // The POST path is independently create-or-resume safe. A transient
          // lookup failure must not strand the owner on a blank page.
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled && !redirected) setCheckingCurrent(false);
      }
    })();

    return () => { cancelled = true; };
  }, [jwt, nav]);

  const handlePick = async (fork: AuditFork) => {
    if (!jwt) { setError('Not signed in'); return; }
    setSubmitting(true); setError(null);
    try {
      const ws = workspaceId ?? await getTargetWorkspaceId();
      if (!ws) throw new Error('no workspace; check signup webhook');
      const { audit } = await createAudit(jwt, { workspace_id: ws, audit_fork: fork });
      nav(`/audits/${audit.id}`, { replace: true });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setSubmitting(false);
    }
  };

  if (checkingCurrent) {
    return <div className="p-8 text-graphite">Loading…</div>;
  }

  return (
    <div className="space-y-10">
      <header className="max-w-3xl space-y-3">
        <p className="text-sm font-semibold text-coral uppercase tracking-[0.18em]">Step 1 of 4</p>
        <h1 className="font-display text-4xl font-semibold tracking-tight text-ink">Let's get started</h1>
        <p className="max-w-2xl text-lg leading-relaxed text-graphite">
          Pick the path that matches where you are. Both paths lead to the same AI
          Workforce Blueprint. From there, one click on Set it up in VibeControl puts your
          agents to work in your business.
        </p>
      </header>
      <AuditForkPicker onPick={handlePick} disabled={submitting} />
      {error && <p className="text-ember text-sm">Error: {error}</p>}
    </div>
  );
}
